/**
 * Sistema de Requisitos - Alertas Modales
 * Muestra requisitos en modales usando SweetAlert2
 * @author Sistema Tránsito Cota
 * @version 3.1 - Corregido cache y endpoints
 */

(function () {
    'use strict';

    // Configuración del módulo
    const CONFIG = {
        CACHE: {},
        DEFAULT_SECTION: 'salida',
        DEFAULT_TITLE: 'Requisitos del Trámite',
        DEBUG: true
    };

    /**
     * Detecta el endpoint base según la URL actual
     */
    function detectEndpointBase() {
        const path = window.location.pathname;
        
        if (path.includes('/salida')) {
            return '/servicios/salida/requisitos/data';
        }
        if (path.includes('/individualizar')) {
            return '/servicios/individualizar/requisitos/data';
        }
        if (path.includes('/impugnacion')) {
            return '/servicios/impugnacion/requisitos/data';
        }
        // Default: salida
        return '/servicios/salida/requisitos/data';
    }

    /**
     * Log condicional según DEBUG
     */
    function log(...args) {
        if (CONFIG.DEBUG) {
            console.log('[Requisitos]', ...args);
        }
    }

    /**
     * Carga requisitos desde el servidor con caché por endpoint
     * El cache es específico por URL para evitar conflictos entre módulos
     */
    async function loadRequisitos(section = CONFIG.DEFAULT_SECTION) {
        // Detectar endpoint actual (puede cambiar si el usuario navega entre páginas)
        const endpoint = detectEndpointBase();
        const cacheKey = `${endpoint}|${section}`;
        
        // Verificar cache primero
        if (CONFIG.CACHE[cacheKey]) {
          //  log('✅ Cache hit:', cacheKey);
            return CONFIG.CACHE[cacheKey];
        }

        try {
            const url = `${endpoint}/${encodeURIComponent(section)}`;
          //  log('🔄 Cargando requisitos desde:', url);

            const response = await fetch(url, {
                method: 'GET',
                headers: { 
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            //log('✅ Datos recibidos:', data);

            // Convertir objeto a array si es necesario
            const items = Array.isArray(data) ? data : Object.values(data);

            // Cachear resultado con clave única
            CONFIG.CACHE[cacheKey] = items;
            
            // Log del estado del cache
          //  log('📦 Cache actual:', Object.keys(CONFIG.CACHE));
            
            return items;
        } catch (error) {
         //   console.error('[Requisitos] ❌ Error cargando datos:', error);
            return [];
        }
    }

    /**
     * Renderiza HTML de requisitos
     */
    function renderRequisitosHTML(items) {
        if (!Array.isArray(items) || items.length === 0) {
            return '<p class="text-muted">No hay requisitos disponibles</p>';
        }

        return `
            <div class="requisitos-list" style="max-height: 60vh; overflow-y: auto;">
                <ul class="list-unstyled mb-0">
                    ${items.map(item => `
                        <li class="mb-3 d-flex align-items-start gap-2">
                            <i class="mdi mdi-check-circle text-success fs-5 mt-1"></i>
                            <span>${item}</span>
                        </li>
                    `).join('')}
                </ul>
            </div>
        `;
    }

    /**
     * Muestra modal de requisitos con SweetAlert2
     */
    async function showRequisitosModal(section, options = {}) {
        // Verificar SweetAlert2
        if (typeof window.Swal === 'undefined') {
         //   console.error('[Requisitos] ❌ SweetAlert2 no disponible');
            alert('Error: Sistema de alertas no disponible');
            return;
        }

       // log('🎯 Mostrando modal para sección:', section);

        try {
            // Mostrar loading inicial
            const loadingModal = window.Swal.fire({
                title: 'Cargando...',
                text: 'Obteniendo requisitos',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    window.Swal.showLoading();
                }
            });

            // Cargar datos
            const items = await loadRequisitos(section);

            // IMPORTANTE: Cerrar loading explícitamente
            if (loadingModal && typeof loadingModal.close === 'function') {
                loadingModal.close();
            } else {
                window.Swal.close();
            }

            // Pequeño delay para asegurar que el loading se cerró
            await new Promise(resolve => setTimeout(resolve, 100));

            // Validar items
            if (!items || items.length === 0) {
                //log('⚠️ Sin requisitos para:', section);
                window.Swal.fire({
                    icon: 'info',
                    title: 'Sin requisitos',
                    text: 'No se encontraron requisitos para esta sección',
                    confirmButtonColor: '#2563eb',
                    confirmButtonText: 'Entendido'
                });
                return;
            }

       //     log('✅ Mostrando', items.length, 'requisitos');

            // Renderizar HTML
            const htmlContent = renderRequisitosHTML(items);

            // Mostrar modal final con todos los parámetros necesarios
            window.Swal.fire({
                title: options.title || CONFIG.DEFAULT_TITLE,
                html: htmlContent,
                width: 700,
                padding: '2rem',
                showCloseButton: true,
                showConfirmButton: true,
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#2563eb',
                allowOutsideClick: true,
                allowEscapeKey: true,
                customClass: {
                    popup: 'requisitos-modal',
                    title: 'fw-bold',
                    htmlContainer: 'text-start'
                }
            });

        } catch (error) {
          //  console.error('[Requisitos] ❌ Error en showRequisitosModal:', error);
            // Cerrar cualquier modal abierto
            window.Swal.close();
            // Mostrar error
            window.Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron cargar los requisitos',
                confirmButtonColor: '#2563eb',
                confirmButtonText: 'Entendido'
            });
        }
    }

    /**
     * Event delegation para elementos con clase .js-requisitos
     */
    function initEventDelegation() {
     //   log('🔧 Inicializando event delegation...');

        document.addEventListener('click', function (event) {
            const target = event.target.closest('.js-requisitos');
            
            if (!target) return;

         //   log('👆 Click detectado en requisitos');

            // Prevenir comportamiento por defecto
            event.preventDefault();
            event.stopPropagation();

            // Extraer datos del elemento
            const section = target.dataset.section || target.getAttribute('data-section') || CONFIG.DEFAULT_SECTION;
            const title = target.dataset.title || target.getAttribute('data-title') || CONFIG.DEFAULT_TITLE;

        //    log('📋 Sección:', section, '| Título:', title);
          //  log('🌐 URL actual:', window.location.pathname);
           // log('🔗 Endpoint:', detectEndpointBase());

            // Mostrar modal
            showRequisitosModal(section, { title });
        }, true); // useCapture = true para interceptar antes

       // log('✅ Event delegation inicializado');
    }

    /**
     * Inicialización del módulo
     */
    function init() {
     //   log('🚀 Inicializando módulo de requisitos...');
        
        // Verificar SweetAlert2
        if (typeof window.Swal === 'undefined') {
           // console.error('[Requisitos] ❌ SweetAlert2 no disponible');
            // Reintentar después de 200ms
            setTimeout(init, 200);
            return;
        }

        // Inicializar event delegation
        initEventDelegation();

        // API pública
        window.Requisitos = {
            show: showRequisitosModal,
            load: loadRequisitos,
            clearCache: () => {
                CONFIG.CACHE = {};
              //  log('🗑️ Cache limpiado');
            },
            debug: (enabled) => {
                CONFIG.DEBUG = enabled;
            //    log('🐛 Debug', enabled ? 'activado' : 'desactivado');
            }
        };

        //log('✅ Módulo completamente inicializado');
        //log('📌 Endpoint actual:', detectEndpointBase());
    }

    // Ejecutar inicialización cuando el DOM esté listo
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();
